import {
  Body1,
  Button,
  Caption1,
  Card,
  CardHeader,
  FlatTree,
  FlatTreeItem,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Spinner,
  Text,
  tokens,
  Tooltip,
  TreeItemLayout,
} from '@fluentui/react-components'
import {
  Delete16Regular,
  DocumentTableRegular,
  DocumentTextExtractRegular,
  Eye16Regular,
  Filter16Regular,
  MoreHorizontal16Regular,
  TargetDismissRegular,
} from '@fluentui/react-icons'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HEADER_0, WORKING_DOCUMENT_ID } from '../../constants/files'
import type { SelectedSection } from '../../contexts/SelectedFilesContext'
import {
  useDeleteFile,
  useDocumentName,
  useFilePreviewDialog,
  useSetFileExclusion,
  useWorkingDocument,
} from '../../hooks'
import { useTooltipControl } from '../../hooks/useTooltipControl'
import type { DocumentSectionTree } from '../../types'
import { formatTextSectionLabel } from '../../utils'
import type { HierarchyViewMode } from '../../utils/filter-section-tree'
import { Dialog } from '../Dialog/Dialog'
import { FileIcon } from '../FileIcon/FileIcon'
import { TableFilterModal } from '../TableFiltering/TableFilterModal'
import { useStyles } from './FileCard.style'
import { FileRetentionRow } from './FileRetentionRow'
import { SectionsStateMessage } from './SectionsStateMessage'
import { useFileCardTree } from './useFileCardTree'

interface FileCardProps {
  fileName: string
  fileType: string
  isFileUploading?: boolean
  fileId: string
  sections?: DocumentSectionTree[]
  /**
   * Initial selection from context.
   * undefined = file not in context (nothing checked)
   * []        = entire file selected (root checked)
   * [...]     = partial selection (those sections checked)
   */
  initialSelectedSections?: SelectedSection[]
  /** Called whenever the user changes selection; updates the LLM context */
  onContextChange?: (sections: SelectedSection[] | null, fileName: string) => void
  /** Whether the card's section tree is currently expanded */
  isExpanded: boolean
  /** Fired when the user clicks to toggle expand/collapse */
  onExpandToggle: () => void
  /** Optional loading override for callers that supply sections externally (e.g. working document) */
  isLoading?: boolean
  /** Optional error override for callers that supply sections externally (e.g. working document) */
  isError?: boolean
  /** Whether this file is excluded from context assist. Not applicable to the working document. */
  isExcluded?: boolean
  /** When set, drives file retention UI for filepool files. */
  expiresAt?: string
  hierarchyViewMode?: HierarchyViewMode
}

const FileCard = ({
  fileName,
  fileType,
  isFileUploading = false,
  fileId,
  sections,
  isError: externalIsError,
  isLoading: externalIsLoading,
  initialSelectedSections,
  onContextChange,
  isExpanded,
  onExpandToggle,
  isExcluded = false,
  expiresAt,
  hierarchyViewMode = 'all',
}: FileCardProps) => {
  const styles = useStyles()
  const {
    visibleTooltip,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    clearTooltip,
    tooltipContentClassName,
  } = useTooltipControl()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openSectionMenuId, setOpenSectionMenuId] = useState<string | null>(null)
  const [filteringSectionId, setFilteringSectionId] = useState<string | null>(null)
  const isWorkingDocument = fileId === WORKING_DOCUMENT_ID
  const { t } = useTranslation()
  const { openFilePreviewDialog } = useFilePreviewDialog()
  const { data: workingDocumentTitle } = useDocumentName(isWorkingDocument)
  const cardFileName = isWorkingDocument
    ? (workingDocumentTitle ?? t('files.untitledDocument'))
    : fileName
  const handleContextChange =
    onContextChange !== undefined
      ? (sections: SelectedSection[] | null) => onContextChange(sections, cardFileName)
      : undefined
  const {
    flatTree,
    items,
    checkedMap,
    treeMetadata,
    handleLoadSections,
    cardRef,
    isLoading: internalIsLoading,
    isError: internalIsError,
  } = useFileCardTree({
    fileId,
    fileName,
    isExpanded,
    onExpandToggle,
    initialSelectedSections,
    onContextChange: handleContextChange,
    sections,
    disabled: isFileUploading,
    hierarchyViewMode,
  })
  const isLoading = externalIsLoading ?? internalIsLoading
  const isError = externalIsError ?? internalIsError
  const { mutateAsync: deleteFile, isPending: isDeleting } = useDeleteFile()
  const {
    mutate: setImatchExclusion,
    isPending: isImatchExclusionPending,
    variables: iMatchExclusionVariables,
  } = useSetFileExclusion()
  const { isWorkingDocumentExcluded, setIsWorkingDocumentExcluded } = useWorkingDocument()

  // Working document exclusion is local state; regular files use optimistic API state
  const displayedIsImatchExcluded = isWorkingDocument
    ? isWorkingDocumentExcluded
    : isImatchExclusionPending && iMatchExclusionVariables?.fileId === fileId
      ? iMatchExclusionVariables.isExcluded
      : isExcluded

  const handlePreview = useCallback(() => {
    openFilePreviewDialog({
      fileId,
      fileName: cardFileName,
    })
  }, [openFilePreviewDialog, fileId, cardFileName])

  const handleSectionPreview = useCallback(
    (sectionId: string, sectionTitle: string, sectionType?: 'heading' | 'text' | 'table') => {
      if (!fileId || !sectionId) return
      openFilePreviewDialog({
        fileId,
        fileName: cardFileName,
        sectionId,
        sectionTitle,
        sectionType,
      })
    },
    [openFilePreviewDialog, cardFileName, fileId]
  )

  const handleToggleExclusion = isWorkingDocument
    ? () => setIsWorkingDocumentExcluded(!isWorkingDocumentExcluded)
    : () => setImatchExclusion({ fileId, isExcluded: !displayedIsImatchExcluded })

  const onDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open)
  }, [])

  const onDeleteConfirm = useCallback(async () => {
    await deleteFile({ fileId })
    setIsDialogOpen(false)
  }, [deleteFile, fileId])

  // Fully selected = entire file in context (empty sections array).
  // Partial selection (indeterminate) should not trigger selected styling.
  const isSelected = initialSelectedSections !== undefined && initialSelectedSections.length === 0

  const filteredEmptyMessageKey =
    hierarchyViewMode === 'sections'
      ? 'files.emptySectionsView'
      : hierarchyViewMode === 'tables'
        ? 'files.emptyTablesView'
        : 'files.emptySections'

  return (
    <Card
      ref={cardRef}
      className={mergeClasses(
        styles.card,
        isFileUploading && styles.cardDisabled,
        isWorkingDocument && styles.cardWorkingDocument
      )}
      disabled={isFileUploading}>
      <FlatTree
        {...flatTree.getTreeProps()}
        aria-label={t('files.treeDescription')}
        className={styles.treeContainer}>
        {(() => {
          const flatTreeItems = Array.from(flatTree.items())
          const lastIdx = flatTreeItems.length - 1
          return flatTreeItems.map((flatTreeItem, idx) => {
            const { title, ...treeItemProps } = flatTreeItem.getTreeItemProps()
            // Top level item with file name and actions
            if (treeItemProps.value === fileId) {
              return (
                <FlatTreeItem
                  key={flatTreeItem.value}
                  {...treeItemProps}
                  className={mergeClasses(
                    styles.fileRow,
                    isWorkingDocument && styles.fileRowWorkingDocument,
                    isSelected && styles.treeItemSelected,
                    isExpanded && styles.fileRowExpanded
                  )}
                  itemType="branch"
                  aria-description={t('files.actions.hasActionsHint')}>
                  <TreeItemLayout
                    className={styles.treeItemLayout}
                    onClick={(e) => {
                      // Don't toggle expand when clicking the selection checkbox
                      if ((e.target as HTMLElement).closest('.fui-Checkbox, [role="checkbox"]'))
                        return
                      handleLoadSections()
                    }}>
                    <CardHeader
                      className={mergeClasses(
                        styles.cardHeader,
                        (isMenuOpen || isFileUploading) && styles.cardHeaderActionVisible
                      )}
                      image={
                        !isWorkingDocument ? (
                          <FileIcon
                            fileType={fileType}
                            height={tokens.spacingHorizontalXL}
                            width={tokens.spacingHorizontalXL}
                          />
                        ) : null
                      }
                      header={
                        <div className={styles.titleWrapper}>
                          <div className={styles.titleRow}>
                            <Tooltip
                              content={{
                                children: cardFileName,
                                className: tooltipContentClassName,
                              }}
                              showDelay={0}
                              visible={visibleTooltip === fileId && !isMenuOpen}
                              relationship="description"
                              appearance="inverted">
                              <Body1
                                as="p"
                                className={mergeClasses(
                                  styles.title,
                                  isSelected && styles.titleSelected
                                )}
                                onMouseEnter={() => handleMouseEnter(fileId)}
                                onMouseLeave={() => handleMouseLeave(fileId)}
                                onFocus={() => handleFocus(fileId)}
                                onBlur={() => handleBlur(fileId)}>
                                {cardFileName}
                              </Body1>
                            </Tooltip>
                            {displayedIsImatchExcluded && (
                              <Tooltip
                                content={t('files.excludedLabel')}
                                relationship="label"
                                appearance="inverted">
                                <TargetDismissRegular
                                  aria-label={t('files.excludedLabel')}
                                  className={styles.excludedIcon}
                                />
                              </Tooltip>
                            )}
                          </div>
                          {isWorkingDocument && (
                            <Caption1 as="p" className={styles.title}>
                              {fileName}
                            </Caption1>
                          )}
                          {!isWorkingDocument && !isFileUploading && (
                            <FileRetentionRow fileId={fileId} expiresAt={expiresAt} />
                          )}
                        </div>
                      }
                      action={
                        isFileUploading ? (
                          <Spinner
                            size="tiny"
                            aria-label={t('loading')}
                            className={styles.loadingSpinner}
                          />
                        ) : (
                          <Menu
                            onOpenChange={(_, data) => {
                              setIsMenuOpen(data.open)
                              // When the menu closes, cancel any pending tooltip timer.
                              // Defer so it runs after Fluent UI returns focus to the trigger button
                              // which would otherwise start a new timer via onFocus.
                              if (!data.open) setTimeout(clearTooltip, 0)
                            }}>
                            <MenuTrigger disableButtonEnhancement>
                              <Button
                                appearance="transparent"
                                icon={<MoreHorizontal16Regular />}
                                aria-label={t('files.actions.moreActionsLabel')}
                                className={mergeClasses(
                                  styles.moreActionsButton,
                                  isMenuOpen && styles.moreActionsButtonVisible
                                )}
                                onClick={(e) => {
                                  e.stopPropagation()
                                }}
                              />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                {!isWorkingDocument && (
                                  <MenuItem
                                    icon={<Eye16Regular />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handlePreview()
                                    }}>
                                    {t('files.actions.preview')}
                                  </MenuItem>
                                )}
                                <MenuItem
                                  icon={<TargetDismissRegular />}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleToggleExclusion()
                                  }}
                                  disabled={!isWorkingDocument && isImatchExclusionPending}>
                                  {displayedIsImatchExcluded
                                    ? t('files.actions.includeInImatch')
                                    : t('files.actions.excludeFromImatch')}
                                </MenuItem>
                                {!isWorkingDocument && (
                                  <MenuItem
                                    icon={<Delete16Regular />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setIsDialogOpen(true)
                                    }}>
                                    {t('files.actions.remove')}
                                  </MenuItem>
                                )}
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        )
                      }
                    />
                  </TreeItemLayout>
                </FlatTreeItem>
              )
            }
            // Child items
            const isItemSelected = checkedMap.get(flatTreeItem.value) === true
            const sectionType = treeMetadata.current.get(flatTreeItem.value as string)?.type
            const itemValue = flatTreeItem.value.toString()
            const localizedTitle =
              sectionType === 'text'
                ? formatTextSectionLabel(title, t('files.sectionTextContentLabel'))
                : title === HEADER_0
                  ? t('files.headerZeroLabel')
                  : title
            return (
              <Tooltip
                key={flatTreeItem.value}
                content={{ children: localizedTitle, className: tooltipContentClassName }}
                showDelay={0}
                visible={visibleTooltip === itemValue}
                relationship="label"
                appearance="inverted">
                <FlatTreeItem
                  key={flatTreeItem.value}
                  {...treeItemProps}
                  onMouseEnter={() => handleMouseEnter(itemValue)}
                  onMouseLeave={() => handleMouseLeave(itemValue)}
                  onFocus={() => handleFocus(itemValue)}
                  onBlur={() => handleBlur(itemValue)}
                  className={mergeClasses(
                    styles.treeChildBase,
                    isItemSelected && styles.treeItemSelected,
                    isWorkingDocument && idx === lastIdx && styles.treeChildLastWorkingDocument
                  )}>
                  <TreeItemLayout
                    className={styles.treeItemLayout}
                    iconBefore={
                      sectionType === 'table' ? (
                        <DocumentTableRegular fontSize={20} />
                      ) : sectionType === 'text' ? (
                        <DocumentTextExtractRegular fontSize={20} />
                      ) : undefined
                    }>
                    <Text
                      weight={isItemSelected ? 'semibold' : 'regular'}
                      className={styles.sectionText}>
                      {localizedTitle}
                    </Text>
                    {/* TODO in #4435: allow filtering for working doc too */}
                    {!isWorkingDocument && (
                      <div
                        className={mergeClasses(
                          'section-action-slot', // Stable class for the parent hover/focus selector in treeChildBase
                          styles.sectionActionSlot,
                          openSectionMenuId === itemValue && styles.sectionActionSlotVisible
                        )}>
                        <Menu
                          onOpenChange={(_, data) =>
                            setOpenSectionMenuId(data.open ? itemValue : null)
                          }>
                          <MenuTrigger disableButtonEnhancement>
                            <Button
                              appearance="transparent"
                              icon={<MoreHorizontal16Regular />}
                              aria-label={t('files.actions.moreActionsLabel')}
                              className={mergeClasses(
                                styles.moreActionsButton,
                                openSectionMenuId === itemValue && styles.moreActionsButtonVisible
                              )}
                              onClick={(e) => {
                                e.stopPropagation()
                              }}
                            />
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              <MenuItem
                                icon={<Eye16Regular />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleSectionPreview(
                                    flatTreeItem.value as string,
                                    localizedTitle,
                                    sectionType
                                  )
                                }}>
                                {t('files.actions.preview')}
                              </MenuItem>
                              {sectionType === 'table' && (
                                <MenuItem
                                  icon={<Filter16Regular />}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setFilteringSectionId(flatTreeItem.value as string)
                                  }}>
                                  {t('files.actions.filter')}
                                </MenuItem>
                              )}
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      </div>
                    )}
                  </TreeItemLayout>
                </FlatTreeItem>
              </Tooltip>
            )
          })
        })()}
        {isExpanded && items.length === 1 && (
          <SectionsStateMessage
            isLoading={isLoading}
            isError={isError}
            className={styles.stateMessage}
            emptyMessageKey={filteredEmptyMessageKey}
          />
        )}
      </FlatTree>
      <Dialog
        title={t('dialog.deleteDialogTitle')}
        content={t('dialog.deleteDialogContent', { fileName })}
        primaryButtonText={t('confirm')}
        secondaryButtonText={t('cancel')}
        onConfirm={onDeleteConfirm}
        onOpenChange={onDialogOpenChange}
        isDialogOpen={isDialogOpen}
        isLoading={isDeleting}
        loadingLabel={t('dialog.deleteDialogLoading')}
      />
      <TableFilterModal
        open={filteringSectionId !== null}
        onOpenChange={(open) => {
          if (!open) setFilteringSectionId(null)
        }}
        fileId={fileId}
        sectionId={filteringSectionId ?? ''}
      />
    </Card>
  )
}

export { FileCard }
